import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import { ChipButton } from '@/components/ChipButton';
import { SizedBox } from '@/components/SizedBox';
import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { CasinoTabNavigationProp } from '@/navigation/casino/types';
import { designSystem } from '@/styles/styles';

import { useNonNavigableCategories } from '../hooks/use-non-navigable-categories';
import {
    FAVORITES_TAB_ID,
    FREE_SPINS_TAB_ID,
    LOBBY_TAB_ID,
    isJackpotGroupId,
    isNavigableCategory,
} from '../utils/utils';

const CategoryChipSeparator = () => <SizedBox value={8} />;

const TabBarLoadingSkeleton = () => (
    <Box flexDirection="row" gap="s8">
        {Array.from({ length: 6 }).map((_, index) => (
            <Box key={index} backgroundColor={ds.colorToken.background.neutral.subtler} padding="s2" borderRadius="r10">
                <LoadingSkeleton
                    skeletonColor={designSystem.colors.neutral300}
                    width={85}
                    height={31}
                    borderRadius="r10"
                />
            </Box>
        ))}
    </Box>
);

type TabBarProps = {
    data: Array<{
        id: string;
        name: string;
    }>;
    showFavoritesTab?: boolean;
    showFreeSpinsTab?: boolean;
    selectedId: string | null;
    onSelectId: (id: string) => void;
    isLoading?: boolean;
};

export const TabBar = ({
    data,
    showFavoritesTab = false,
    showFreeSpinsTab = false,
    selectedId,
    onSelectId,
    isLoading = false,
}: TabBarProps) => {
    const navigation = useNavigation<CasinoTabNavigationProp>();
    const flatListRef = useRef<FlatList>(null);
    const nonNavigableCategories = useNonNavigableCategories();
    const filteredData = data.filter(
        item => isNavigableCategory(item.name, nonNavigableCategories) && item.name !== 'Favorites'
    );
    const dataWithExplorer = useMemo(
        () => [
            { id: LOBBY_TAB_ID, name: 'Explore' },
            ...filteredData.filter(item => isJackpotGroupId(item.id)),
            ...(showFreeSpinsTab ? [{ id: FREE_SPINS_TAB_ID, name: 'Free Spins' }] : []),
            ...(showFavoritesTab ? [{ id: FAVORITES_TAB_ID, name: 'Favorites' }] : []),
            ...filteredData.filter(item => !isJackpotGroupId(item.id)),
        ],
        [filteredData, showFavoritesTab, showFreeSpinsTab]
    );

    // Scroll to active tab when selectedId changes
    useEffect(() => {
        if (selectedId && !isLoading) {
            const selectedIndex = dataWithExplorer.findIndex(item => item.id === selectedId);
            if (selectedIndex !== -1) {
                flatListRef.current?.scrollToIndex({
                    index: selectedIndex,
                    animated: true,
                    viewPosition: 0.5,
                });
            }
        }
    }, [selectedId, dataWithExplorer, isLoading]);

    const handlePress = useCallback(
        (id: string, name: string) => {
            if (id === LOBBY_TAB_ID) {
                navigation.setParams({
                    category: undefined,
                });
                onSelectId(LOBBY_TAB_ID);
            } else {
                navigation.setParams({
                    category: name,
                });
                onSelectId(id);
            }
        },
        [navigation, onSelectId]
    );

    return (
        <Box mb="s12">
            {isLoading ? (
                <TabBarLoadingSkeleton />
            ) : (
                <FlatList
                    ref={flatListRef}
                    contentContainerStyle={styles.chipListContainer}
                    data={dataWithExplorer}
                    renderItem={({ item }) => {
                        return (
                            <ChipButton
                                key={item.id}
                                label={item.name}
                                onPress={() => handlePress(item.id, item.name)}
                                active={selectedId === item.id}
                            />
                        );
                    }}
                    ItemSeparatorComponent={CategoryChipSeparator}
                    showsHorizontalScrollIndicator={false}
                    horizontal
                    ListEmptyComponent={TabBarLoadingSkeleton}
                    scrollEnabled={true}
                    onScrollToIndexFailed={info => {
                        flatListRef.current?.scrollToOffset({
                            offset: info.averageItemLength * info.index,
                            animated: true,
                        });
                    }}
                />
            )}
        </Box>
    );
};

const styles = StyleSheet.create({
    chipListContainer: {
        flexGrow: 1,
        paddingHorizontal: 16,
    },
});
